import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {ListContentModel} from '../../../shared/models/list-content.model';
import {ActivityReportService} from '../../activity-report/services/activity-report.service';
import {FeesService} from '../../fees/services/fees.service';
import {OnCallService} from '../../on-call/services/on-call.service';
import {ActivityTypeEnum} from '../models/activity-type.enum';
import {DuePaymentTypeEnum} from '../models/activity.model';
import {ElectronicInvoiceLine} from '../models/electronic-invoice-line.model';
import {InvoiceService} from './invoice.service';

@Injectable({
  providedIn: 'root'
})
export class ElectronicInvoiceToGenerateService {
  private paymentDelayCache = new Map<string, number>();

  constructor(
    private activityReportService: ActivityReportService,
    private invoiceService: InvoiceService,
    private feesService: FeesService,
    private onCallService: OnCallService,
    private translateService: TranslateService,
  ) {}

  loadLines(): Observable<ElectronicInvoiceLine[]> {
    const page = 0;
    const size = 2000;
    return forkJoin({
      sheets: this.activityReportService.getActivitySheetsWaitingForInvoiceGeneration(page, size, 'accountingMonth,asc', null)
        .pipe(catchError(() => of({content: []} as ListContentModel))),
      duePayments: this.invoiceService.getDuePaymentsWaitingForGeneration(page, size, 'id,asc', null)
        .pipe(catchError(() => of({content: []} as ListContentModel))),
      onCalls: this.invoiceService.getOnCallsWaitingForGeneration(page, size, 'date,asc', null)
        .pipe(catchError(() => of({content: []} as ListContentModel))),
      fees: this.invoiceService.getFeesWaitingForGeneration(page, size, 'date,asc', null)
        .pipe(catchError(() => of({content: []} as ListContentModel))),
      regulations: this.invoiceService.getRegulationsWaitingForGeneration(page, size, 'id,asc', null)
        .pipe(catchError(() => of({content: []} as ListContentModel))),
    }).pipe(
      switchMap(({sheets, duePayments, onCalls, fees, regulations}) => {
        const sheetLines = this.mapActivitySheets((sheets as ListContentModel)?.content || []);
        const dueLines$ = this.mapDuePayments((duePayments as ListContentModel)?.content || []);
        const onCallLines$ = this.mapOnCalls((onCalls as ListContentModel)?.content || []);
        const feeLines$ = this.mapFees((fees as ListContentModel)?.content || []);
        const regulationLines$ = this.mapRegulations((regulations as ListContentModel)?.content || []);
        return forkJoin({
          dues: dueLines$,
          onCalls: onCallLines$,
          fees: feeLines$,
          regulations: regulationLines$,
        }).pipe(
          map(({dues, onCalls: oc, fees: feeRows, regulations: regs}) =>
            [...sheetLines, ...dues, ...oc, ...feeRows, ...regs]
          ),
          switchMap(lines => this.enrichPaymentDelays(lines))
        );
      })
    );
  }

  private mapActivitySheets(items: any[]): ElectronicInvoiceLine[] {
    return items.map(item => {
      const quantity = Number(item.sum ?? 0);
      const unitPrice = Number(item.unitPrice ?? 0);
      return {
        activityId: `TS_${item.id}`,
        purchaseOrderNumber: item.purchaseOrderNumber || '',
        clientName: item.buyerCompanyName || item.buyerName || '',
        periodLabel: this.formatCraPeriod(item),
        periodKey: this.toPeriodKey(item.startDate || item.accountingMonth || item.period),
        unitPrice,
        quantity,
        amountExcludingTax: this.round2(unitPrice * quantity),
        type: ActivityTypeEnum.ACTIVITY_SHEET,
        currency: item.currency || 'EUR',
        requestId: String(item.requestId || ''),
      };
    });
  }

  private mapDuePayments(items: any[]): Observable<ElectronicInvoiceLine[]> {
    if (!items.length) {
      return of([]);
    }
    return forkJoin(items.map(item =>
      this.invoiceService.getDuePaymentsById(item.id).pipe(
        catchError(() => of(item)),
        map((detail: any) => {
          const unitPrice = Number(detail?.amount ?? item.amount ?? 0);
          const quantity = 1;
          const duePaymentType = detail?.type || item.type || item.duePaymentType;
          return {
            activityId: `DP_${item.id}`,
            purchaseOrderNumber: item.purchaseOrderNumber || detail?.purchaseOrderNumber || '',
            clientName: item.buyerName || item.client || detail?.buyerName || '',
            periodLabel: this.formatDuePaymentPeriod(detail || item, duePaymentType),
            periodKey: this.toPeriodKey(detail?.dueDate || item.dueDate || item.accountingMonth),
            unitPrice,
            quantity,
            amountExcludingTax: this.round2(unitPrice * quantity),
            type: ActivityTypeEnum.DUE_PAYMENT,
            duePaymentType,
            currency: item.currency || 'EUR',
            requestId: String(item.requestId || detail?.requestId || ''),
          } as ElectronicInvoiceLine;
        })
      )
    ));
  }

  private mapFees(items: any[]): Observable<ElectronicInvoiceLine[]> {
    const ids: Array<{id: number | string; group: any}> = [];
    items.forEach(group => {
      (group.ids || []).forEach((id: number | string) => ids.push({id, group}));
    });
    if (!ids.length) {
      return of([]);
    }
    return forkJoin(ids.map(({id, group}) =>
      this.feesService.getFeeById(Number(id)).pipe(
        catchError(() => of(null)),
        map((detail: any) => {
          // Billable amount (TTC for parking / non-deductible VAT); not expense-note HT.
          const unitPrice = Number(
            detail?.invoicedPrice
            ?? detail?.amountExcludingTax
            ?? detail?.amount
            ?? 0
          );
          const quantity = 1;
          const date = detail?.date || group.period;
          return {
            activityId: `FEE_${id}`,
            purchaseOrderNumber: detail?.purchaseOrderNumber || group.purchaseOrderNumber || '',
            clientName: detail?.buyerCompanyName || group.client || group.buyerCompanyName || '',
            periodLabel: this.formatSingleOrRangeDate(date, date),
            periodKey: this.toPeriodKey(date),
            unitPrice,
            quantity,
            amountExcludingTax: this.round2(unitPrice * quantity),
            type: ActivityTypeEnum.FEE,
            currency: detail?.currency || group.currency || 'EUR',
            requestId: String(group.requestId || ''),
          } as ElectronicInvoiceLine;
        })
      )
    )).pipe(map(rows => rows.filter(Boolean)));
  }

  private mapOnCalls(items: any[]): Observable<ElectronicInvoiceLine[]> {
    const ids: Array<{id: number | string; group: any}> = [];
    items.forEach(group => {
      (group.ids || []).forEach((id: number | string) => ids.push({id, group}));
    });
    if (!ids.length) {
      return of([]);
    }
    return forkJoin(ids.map(({id, group}) =>
      this.onCallService.getOnCallById(id).pipe(
        catchError(() => of(null)),
        map((detail: any) => {
          const amount = Number(
            detail?.priceAmount
            ?? detail?.totalPriceExcludingVat
            ?? group.totalPriceExcludingVat
            ?? 0
          );
          const quantity = Number(detail?.quantity ?? 1);
          const unitPrice = quantity !== 0 ? this.round2(amount / quantity) : amount;
          const date = detail?.date || group.period;
          return {
            activityId: `OC_${id}`,
            purchaseOrderNumber: detail?.purchaseOrderNumber || group.purchaseOrderNumber || '',
            clientName: detail?.client || group.client || group.buyerCompanyName || '',
            periodLabel: this.formatSingleOrRangeDate(date, date),
            periodKey: this.toPeriodKey(date),
            unitPrice,
            quantity,
            amountExcludingTax: this.round2(unitPrice * quantity),
            type: ActivityTypeEnum.ON_CALL,
            currency: group.currency || 'EUR',
            requestId: String(group.requestId || ''),
          } as ElectronicInvoiceLine;
        })
      )
    )).pipe(map(rows => rows.filter(Boolean)));
  }

  private mapRegulations(items: any[]): Observable<ElectronicInvoiceLine[]> {
    const ids: Array<{id: string; group: any}> = [];
    items.forEach(group => {
      (group.regulationIds || group.ids || []).forEach((id: string) => ids.push({id: String(id), group}));
    });
    if (!ids.length) {
      return of([]);
    }
    return forkJoin(ids.map(({id, group}) =>
      this.invoiceService.getRegulationById(id).pipe(
        catchError(() => of(null)),
        map((detail: any) => {
          const unitPrice = (detail?.requestRegulationDetails || [])
            .reduce((sum: number, d: any) => sum + Number(d.regulationAmount || 0), 0);
          const quantity = 1;
          return {
            activityId: `REG_${id}`,
            purchaseOrderNumber: detail?.purchaseOrderNumber || group.purchaseOrderNumber || '',
            clientName: detail?.clientName || group.clientName || group.client || '',
            periodLabel: '',
            periodKey: undefined,
            unitPrice,
            quantity,
            amountExcludingTax: this.round2(unitPrice * quantity),
            type: ActivityTypeEnum.REGULATION,
            currency: group.currency || 'EUR',
            requestId: String(group.requestId || detail?.requestId || ''),
          } as ElectronicInvoiceLine;
        })
      )
    )).pipe(map(rows => rows.filter(Boolean)));
  }

  private enrichPaymentDelays(lines: ElectronicInvoiceLine[]): Observable<ElectronicInvoiceLine[]> {
    const requestIds = [...new Set(lines.map(l => l.requestId).filter(Boolean))];
    if (!requestIds.length) {
      return of(lines);
    }
    const missing = requestIds.filter(id => !this.paymentDelayCache.has(id));
    const loads$ = missing.length
      ? forkJoin(missing.map(id =>
          this.invoiceService.getSupplierInvoiceConfigByRequestId(id).pipe(
            catchError(() => of(null)),
            map(cfg => {
              this.paymentDelayCache.set(id, Number(cfg?.billableUnder ?? 0));
              return id;
            })
          )
        ))
      : of([]);
    return loads$.pipe(
      map(() => lines.map(line => ({
        ...line,
        paymentDelayDays: line.requestId ? this.paymentDelayCache.get(line.requestId) : undefined,
      })))
    );
  }

  private formatCraPeriod(item: any): string {
    const start = item.startDate ? new Date(item.startDate) : this.parseMonthStart(item.accountingMonth);
    const end = item.endDate ? new Date(item.endDate) : this.parseMonthEnd(item.accountingMonth);
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return item.accountingMonth || item.period || '';
    }
    if (this.isFullCalendarMonth(start, end)) {
      return start.toLocaleDateString(this.locale(), {month: 'long', year: 'numeric'});
    }
    const opts: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'long', year: 'numeric'};
    return `${start.toLocaleDateString(this.locale(), opts)} - ${end.toLocaleDateString(this.locale(), opts)}`;
  }

  private formatDuePaymentPeriod(item: any, type: any): string {
    if (type === DuePaymentTypeEnum.EVENT || type === 'EVENT') {
      return '';
    }
    return this.formatSingleOrRangeDate(item.dueDate || item.date, item.dueDate || item.date);
  }

  private formatSingleOrRangeDate(startRaw: any, endRaw: any): string {
    const start = startRaw ? new Date(startRaw) : null;
    const end = endRaw ? new Date(endRaw) : null;
    if (!start || isNaN(start.getTime())) {
      return '';
    }
    const locale = this.locale();
    if (!end || isNaN(end.getTime()) || this.isSameDay(start, end)) {
      return start.toLocaleDateString(locale);
    }
    return `${start.toLocaleDateString(locale)} - ${end.toLocaleDateString(locale)}`;
  }

  private toPeriodKey(value: any): string | undefined {
    if (!value) {
      return undefined;
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}/.test(value)) {
      return value.substring(0, 7);
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return undefined;
    }
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}`;
  }

  private parseMonthStart(month: string): Date | null {
    if (!month || !/^\d{4}-\d{2}/.test(month)) {
      return null;
    }
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1);
  }

  private parseMonthEnd(month: string): Date | null {
    const start = this.parseMonthStart(month);
    if (!start) {
      return null;
    }
    return new Date(start.getFullYear(), start.getMonth() + 1, 0);
  }

  private isFullCalendarMonth(start: Date, end: Date): boolean {
    return start.getDate() === 1
      && end.getDate() === new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
      && start.getMonth() === end.getMonth()
      && start.getFullYear() === end.getFullYear();
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
  }

  private locale(): string {
    return this.translateService.currentLang === 'FR' ? 'fr-FR' : 'en-GB';
  }

  private round2(value: number): number {
    return Math.round((Number(value) || 0) * 100) / 100;
  }
}
